import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  type AttentionList,
  type NewSubscriptionAttention,
  type YearlySoonAttention,
  useAttentionQuery,
  useDismissAttentionMutation,
} from '../../entities/attention/index.js';
import { useOccurrenceStatusMutation } from '../../entities/occurrence/index.js';
import {
  useDeletePaymentMutation,
  useMarkPaymentSeenMutation,
  usePaymentStatusMutation,
} from '../../entities/payment/index.js';
import { occurrencePayPath, paymentDetailPath } from '../../features/navigation/index.js';
import { BackButton, openExternalLink } from '../../shared/telegram/index.js';
import {
  ErrorState,
  List,
  PageHeader,
  Screen,
  Sheet,
  Spinner,
  type SheetAction,
} from '../../shared/ui/index.js';
import { AttentionRow } from './attention-row/attention-row.js';
import { CalmState } from './calm-state/calm-state.js';
import { actionsOf, openerOf, type AttentionHandlers } from './lib/attention-actions.js';
import { attentionViewOf } from './lib/attention-view.js';
import styles from './attention-page.module.css';

type PressedAction = {
  itemId: string;
  actionId: string;
};

const notSubscriptionTitleOf = (item: NewSubscriptionAttention): string =>
  `Це не підписка? «${item.name}» буде видалено разом з історією, а місяці, які підтвердив банк, повернуться у звичайні витрати. Цей продавець більше не стане підпискою.`;

const cancelTitleOf = (item: YearlySoonAttention): string => {
  const question = `Скасувати підписку «${item.name}»? Кнопка «назад» це не поверне.`;

  return item.cancelUrl === null
    ? question
    : `${question} Сам сервіс треба повідомити окремо — на його сайті.`;
};

type AttentionContentProps = {
  list: AttentionList | undefined;
  failureMessage: string | null;
  handlers: AttentionHandlers;
  pressed: PressedAction | null;
  isActing: boolean;
  onRetry: () => void;
  onPress: (itemId: string, actionId: string) => void;
};

const AttentionContent = ({
  list,
  failureMessage,
  handlers,
  pressed,
  isActing,
  onRetry,
  onPress,
}: AttentionContentProps) => {
  if (list === undefined && failureMessage !== null) {
    return (
      <ErrorState
        title="Не вдалося завантажити клопоти"
        message={failureMessage}
        onRetry={onRetry}
      />
    );
  }

  if (list === undefined) {
    return (
      <div className={styles.centered}>
        <Spinner label="Завантаження" />
      </div>
    );
  }

  if (list.items.length === 0) {
    return <CalmState today={list.today} onOpen={handlers.onOpenPayment} />;
  }

  return (
    <List>
      {list.items.map((item) => (
        <AttentionRow
          key={item.id}
          view={attentionViewOf(item)}
          actions={actionsOf(item, handlers)}
          busyActionId={pressed?.itemId === item.id ? pressed.actionId : null}
          isActing={isActing}
          onPress={(actionId) => {
            onPress(item.id, actionId);
          }}
          onOpen={openerOf(item, handlers)}
        />
      ))}
    </List>
  );
};

export const AttentionPage = () => {
  const [pressed, setPressed] = useState<PressedAction | null>(null);
  const [notSubscriptionItem, setNotSubscriptionItem] = useState<NewSubscriptionAttention | null>(
    null,
  );
  const [cancelItem, setCancelItem] = useState<YearlySoonAttention | null>(null);
  const navigate = useNavigate();
  const attention = useAttentionQuery();
  const dismissMutation = useDismissAttentionMutation();
  const markSeenMutation = useMarkPaymentSeenMutation();
  const deleteMutation = useDeletePaymentMutation();
  const statusMutation = usePaymentStatusMutation();
  const periodMutation = useOccurrenceStatusMutation();

  const isActing =
    dismissMutation.isPending ||
    markSeenMutation.isPending ||
    deleteMutation.isPending ||
    statusMutation.isPending ||
    periodMutation.isPending;
  const actionFailure =
    dismissMutation.error ??
    markSeenMutation.error ??
    deleteMutation.error ??
    statusMutation.error ??
    periodMutation.error;
  const isSheetOpen = notSubscriptionItem !== null || cancelItem !== null;

  const resetFailures = (): void => {
    dismissMutation.reset();
    markSeenMutation.reset();
    deleteMutation.reset();
    statusMutation.reset();
    periodMutation.reset();
  };

  const closeSheets = (): void => {
    setNotSubscriptionItem(null);
    setCancelItem(null);
  };

  const handlers: AttentionHandlers = {
    onDismiss: (item) => {
      dismissMutation.mutate(item.id);
    },
    onConfirmSubscription: (item) => {
      markSeenMutation.mutate(item.paymentId);
    },
    onAskNotSubscription: setNotSubscriptionItem,
    onAskCancel: setCancelItem,
    onPay: (occurrenceId) => {
      void navigate(occurrencePayPath(occurrenceId));
    },
    onVoid: (occurrenceId) => {
      periodMutation.mutate({ action: 'void', id: occurrenceId });
    },
    onOpenPayment: (paymentId) => {
      void navigate(paymentDetailPath(paymentId));
    },
  };

  const removeAsNotSubscription = (item: NewSubscriptionAttention): void => {
    resetFailures();
    deleteMutation.mutate(item.paymentId, { onSettled: closeSheets });
  };

  const cancelSubscription = (item: YearlySoonAttention): void => {
    resetFailures();
    statusMutation.mutate(
      { action: 'cancel', id: item.paymentId, body: { reason: null } },
      { onSettled: closeSheets },
    );
  };

  const notSubscriptionActions: SheetAction[] =
    notSubscriptionItem === null
      ? []
      : [
          {
            id: 'confirm-not-subscription',
            label: 'Так, це не підписка',
            danger: true,
            disabled: isActing,
            onSelect: () => {
              removeAsNotSubscription(notSubscriptionItem);
            },
          },
        ];

  const cancelUrl = cancelItem?.cancelUrl ?? null;

  const cancelSiteActions: SheetAction[] =
    cancelUrl === null
      ? []
      : [
          {
            id: 'open-cancel-site',
            label: 'Відкрити сайт сервісу',
            onSelect: () => {
              openExternalLink(cancelUrl);
            },
          },
        ];

  const cancelActions: SheetAction[] =
    cancelItem === null
      ? []
      : [
          {
            id: 'confirm-cancel',
            label: 'Так, скасувати підписку',
            danger: true,
            disabled: isActing,
            onSelect: () => {
              cancelSubscription(cancelItem);
            },
          },
          ...cancelSiteActions,
        ];

  return (
    <Screen>
      <div className={styles.content}>
        <PageHeader title="Клопоти" />

        {actionFailure !== null && (
          <p className={styles.actionError} role="alert">
            {actionFailure.message}
          </p>
        )}

        <AttentionContent
          list={attention.data}
          failureMessage={attention.error === null ? null : attention.error.message}
          handlers={handlers}
          pressed={pressed}
          isActing={isActing}
          onRetry={() => {
            void attention.refetch();
          }}
          onPress={(itemId, actionId) => {
            resetFailures();
            setPressed({ itemId, actionId });
          }}
        />
      </div>

      <Sheet
        open={notSubscriptionItem !== null}
        actions={notSubscriptionActions}
        title={
          notSubscriptionItem === null ? undefined : notSubscriptionTitleOf(notSubscriptionItem)
        }
        closeLabel="Ні, лишити"
        onClose={closeSheets}
      />

      <Sheet
        open={cancelItem !== null}
        actions={cancelActions}
        title={cancelItem === null ? undefined : cancelTitleOf(cancelItem)}
        closeLabel="Ні, лишити"
        onClose={closeSheets}
      />

      {isSheetOpen && <BackButton onBack={closeSheets} />}
    </Screen>
  );
};
